/**
 * StackedAreaChart — % composition of analytes over time.
 * Native hand-rolled SVG (no Recharts). d3-stack for the stacking math,
 * d3-area with y0/y1 accessors for each layer.
 */

import { useState, useMemo, useCallback, useRef, useEffect, useId } from 'react';
import { scaleTime, scaleLinear } from 'd3-scale';
import { stack as d3Stack, area as d3Area, curveMonotoneX } from 'd3-shape';
import type { AnalyticalTimeseriesResponse, ConcentrationUnit } from '@/api/dataVault';
import { ANALYTE_COLORS } from '../../chartTheme';
import { ChartTooltip } from '../../ChartTooltip';
import { useChartView } from '../../interactions/useChartView';
import { ChartTimeRangeRow } from '../../ChartTimeRangeRow';
import { defaultHiddenAnalytes } from '../../chartDefaults';

interface Props {
  data: AnalyticalTimeseriesResponse;
  unit: ConcentrationUnit;
}

function convertConc(val: number | null, _unit: ConcentrationUnit): number | null {
  if (val === null) return null;
  return val;
}

interface Row {
  ts: number;
  [code: string]: number;
}

const MARGIN = { top: 12, right: 24, bottom: 40, left: 60 };
const CHART_HEIGHT = 420;

export function StackedAreaChart({ data, unit }: Props) {
  // Stacked area shows analyte composition over time — needs every analyte.
  const [hiddenAnalytes, setHiddenAnalytes] = useState<Set<string>>(new Set());

  const allAnalytes = useMemo(() => {
    const map = new Map<string, string>();
    for (const well of data.wells) {
      for (const [code, analyte] of Object.entries(well.analytes)) {
        if (!map.has(code)) map.set(code, analyte.compound);
      }
    }
    return Array.from(map.entries()).map(([code, compound], i) => ({
      code,
      compound,
      color: ANALYTE_COLORS[i % ANALYTE_COLORS.length],
    }));
  }, [data.wells]);

  const visibleAnalytes = useMemo(
    () => allAnalytes.filter((a) => !hiddenAnalytes.has(a.code)),
    [allAnalytes, hiddenAnalytes],
  );

  const chartData: Row[] = useMemo(() => {
    const dateMap = new Map<number, Map<string, number>>();
    for (const well of data.wells) {
      for (const a of visibleAnalytes) {
        const analyte = well.analytes[a.code];
        if (!analyte) continue;
        for (const m of analyte.measurements) {
          const conc = convertConc(m.concentration, unit);
          if (conc === null || conc <= 0) continue;
          const ts = new Date(m.date).getTime();
          if (!dateMap.has(ts)) dateMap.set(ts, new Map());
          const row = dateMap.get(ts)!;
          row.set(a.code, (row.get(a.code) ?? 0) + conc);
        }
      }
    }
    const sortedTs = Array.from(dateMap.keys()).sort((a, b) => a - b);
    return sortedTs.map((ts) => {
      const raw = dateMap.get(ts)!;
      const total = Array.from(raw.values()).reduce((s, v) => s + v, 0);
      const row: Row = { ts };
      for (const a of visibleAnalytes) {
        const val = raw.get(a.code) ?? 0;
        row[a.code] = total > 0 ? (val / total) * 100 : 0;
      }
      return row;
    });
  }, [data.wells, visibleAnalytes, unit]);

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(800);
  const clipId = `sa-clip-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(Math.round(w));
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerHeight = Math.max(0, CHART_HEIGHT - MARGIN.top - MARGIN.bottom);

  const baseXDomain = useMemo<readonly [number, number]>(() => {
    if (chartData.length === 0) return [0, 1];
    if (chartData.length === 1) {
      const t = chartData[0].ts;
      return [t - 86_400_000, t + 86_400_000];
    }
    return [chartData[0].ts, chartData[chartData.length - 1].ts];
  }, [chartData]);

  const baseYDomain = useMemo<readonly [number, number]>(() => [0, 100], []);

  const xPxToDataRef = useRef<(px: number) => number>(() => 0);
  const yPxToDataRef = useRef<(py: number) => number>(() => 0);
  const view = useChartView(
    svgRef,
    (px) => xPxToDataRef.current(px),
    (py) => yPxToDataRef.current(py),
    {
      baseXDomain,
      baseYDomain,
      dragZoomAxes: 'x',
      chartArea: {
        top: MARGIN.top,
        bottom: MARGIN.top + innerHeight,
        left: MARGIN.left,
        right: MARGIN.left + innerWidth,
      },
    },
  );

  const xScale = useMemo(
    () => scaleTime().domain([view.xDomain[0], view.xDomain[1]]).range([MARGIN.left, MARGIN.left + innerWidth]),
    [view.xDomain, innerWidth],
  );
  const yScale = useMemo(
    () => scaleLinear().domain([view.yDomain[0], view.yDomain[1]]).range([MARGIN.top + innerHeight, MARGIN.top]),
    [view.yDomain, innerHeight],
  );
  xPxToDataRef.current = (px: number) => xScale.invert(px).getTime();
  yPxToDataRef.current = (py: number) => yScale.invert(py);

  const series = useMemo(() => {
    if (chartData.length === 0 || visibleAnalytes.length === 0) return [];
    const stacker = d3Stack<Row, string>()
      .keys(visibleAnalytes.map((a) => a.code))
      .value((d, key) => d[key] ?? 0);
    return stacker(chartData);
  }, [chartData, visibleAnalytes]);

  const areaGen = useMemo(
    () =>
      d3Area<[number, number] & { data: Row }>()
        .x((d) => xScale((d as any).data.ts))
        .y0((d) => yScale(d[0]))
        .y1((d) => yScale(d[1]))
        .curve(curveMonotoneX),
    [xScale, yScale],
  );

  const xTicks = useMemo(() => xScale.ticks(6), [xScale]);
  const yTicks = useMemo(() => [0, 25, 50, 75, 100], []);

  const formatX = (d: Date) => `${d.getMonth() + 1}/${d.getFullYear().toString().slice(2)}`;

  // Hover
  interface HoverState { row: Row; clientX: number; clientY: number }
  const [hover, setHover] = useState<HoverState | null>(null);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      const svg = svgRef.current; if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const localX = e.clientX - rect.left;
      let bestRow: Row | null = null;
      let bestDx = Infinity;
      for (const row of chartData) {
        const px = xScale(row.ts);
        const dx = Math.abs(px - localX);
        if (dx < bestDx) {
          bestDx = dx;
          bestRow = row;
        }
      }
      if (bestRow && bestDx < 30) {
        setHover({ row: bestRow, clientX: e.clientX, clientY: e.clientY });
      } else {
        setHover(null);
      }
    },
    [chartData, xScale],
  );
  const handleMouseLeave = useCallback(() => setHover(null), []);

  const toggleAnalyte = useCallback((code: string) => {
    setHiddenAnalytes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }, []);

  if (!data.wells.length) {
    return (
      <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
        NO ANALYTICAL DATA AVAILABLE
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs mb-3" style={{ color: 'var(--dv-text-muted)' }}>
        Stacked area chart showing how analyte concentrations change over time as a 100% composition.
        Each band's height represents that analyte's share of total contamination at that date.
      </p>

      <div className="flex flex-wrap gap-1.5">
        <span className="text-[9px] text-mono-500 tracking-wider self-center mr-1">ANALYTES:</span>
        {allAnalytes.map((a) => {
          const hidden = hiddenAnalytes.has(a.code);
          return (
            <button
              key={a.code}
              onClick={() => toggleAnalyte(a.code)}
              className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border transition-opacity"
              style={{
                borderColor: 'var(--dv-border-subtle)',
                color: hidden ? 'var(--dv-text-muted)' : 'var(--dv-text-primary)',
                opacity: hidden ? 0.4 : 1,
              }}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{
                  backgroundColor: hidden ? 'transparent' : a.color,
                  border: hidden ? `1px solid ${a.color}` : 'none',
                }}
              />
              {a.compound}
            </button>
          );
        })}
      </div>

      <ChartTimeRangeRow
        dataMinMs={baseXDomain[0]}
        dataMaxMs={baseXDomain[1]}
        activeDomain={view.xDomain}
        onSelect={(d) => view.setXDomain(d)}
        isZoomed={view.isZoomed}
        onReset={view.reset}
      />

      <div ref={containerRef} role="img" aria-label="Stacked area chart of analyte composition over time">
        {chartData.length === 0 || visibleAnalytes.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
            SELECT ANALYTES TO DISPLAY
          </div>
        ) : (
          <svg
            ref={svgRef}
            width={width || '100%'}
            height={CHART_HEIGHT}
            style={{ display: 'block', userSelect: 'none', touchAction: 'none', cursor: 'crosshair' }}
          >
            <defs>
              <clipPath id={clipId}>
                <rect x={MARGIN.left} y={MARGIN.top} width={innerWidth} height={innerHeight} />
              </clipPath>
            </defs>

            <g clipPath={`url(#${clipId})`}>
              {/* Gridlines */}
              {yTicks.map((t) => {
                const y = yScale(t);
                return (
                  <line
                    key={`grid-${t}`}
                    x1={MARGIN.left}
                    x2={MARGIN.left + innerWidth}
                    y1={y}
                    y2={y}
                    stroke="var(--dv-chart-grid)"
                    strokeDasharray="3 3"
                    strokeWidth={1}
                  />
                );
              })}

              {/* Stacked area bands */}
              {series.map((s, i) => {
                const analyte = visibleAnalytes.find((a) => a.code === s.key);
                if (!analyte) return null;
                return (
                  <path
                    key={`area-${s.key}-${i}`}
                    d={areaGen(s as unknown as Array<[number, number] & { data: Row }>) ?? ''}
                    fill={analyte.color}
                    fillOpacity={0.72}
                    stroke={analyte.color}
                    strokeWidth={1}
                  />
                );
              })}
            </g>

            {/* Y-axis */}
            <line x1={MARGIN.left} x2={MARGIN.left} y1={MARGIN.top} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
            {yTicks.map((t) => {
              const y = yScale(t);
              return (
                <g key={`yt-${t}`}>
                  <line x1={MARGIN.left - 4} x2={MARGIN.left} y1={y} y2={y} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                  <text x={MARGIN.left - 8} y={y} textAnchor="end" dominantBaseline="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {t}%
                  </text>
                </g>
              );
            })}
            <text transform={`translate(${MARGIN.left - 44}, ${MARGIN.top + innerHeight / 2}) rotate(-90)`} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)">
              % of Total
            </text>

            {/* X-axis */}
            <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
            {xTicks.map((t, i) => {
              const x = xScale(t);
              return (
                <g key={`xt-${i}`}>
                  <line x1={x} x2={x} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight + 4} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                  <text x={x} y={MARGIN.top + innerHeight + 16} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatX(t)}
                  </text>
                </g>
              );
            })}

            {/* Rect-zoom preview */}
            {view.rectPreview && (
              <rect
                x={view.rectPreview.x0}
                y={view.rectPreview.y0}
                width={view.rectPreview.x1 - view.rectPreview.x0}
                height={view.rectPreview.y1 - view.rectPreview.y0}
                fill="rgba(16, 185, 129, 0.12)"
                stroke="var(--dv-accent-emerald, #10b981)"
                strokeWidth={1}
                strokeDasharray="3 3"
                pointerEvents="none"
              />
            )}

            <rect
              x={MARGIN.left}
              y={MARGIN.top}
              width={innerWidth}
              height={innerHeight}
              fill="transparent"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            />
          </svg>
        )}
      </div>

      {hover && (
        <ChartTooltip visible x={hover.clientX} y={hover.clientY} placement="top">
          <div style={{ minWidth: 200, fontSize: 11 }}>
            <div style={{ fontSize: 10, color: 'var(--dv-tooltip-label, rgba(255,255,255,0.7))', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4, paddingBottom: 4, borderBottom: '1px solid var(--dv-tooltip-border, rgba(255,255,255,0.08))' }}>
              {new Date(hover.row.ts).toLocaleDateString()}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {visibleAnalytes.map((a) => {
                const v = hover.row[a.code] ?? 0;
                if (v === 0) return null;
                return (
                  <div key={a.code} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 999, background: a.color }} />
                    <span style={{ flex: 1 }}>{a.compound}</span>
                    <span style={{ fontFamily: 'var(--dv-font-mono, ui-monospace, monospace)', fontWeight: 600 }}>
                      {v.toFixed(1)}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}
